import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Map, 
  Zap, 
  FileCheck, 
  LayoutGrid, 
  Check, 
  PackageCheck, 
  LifeBuoy,
  ArrowLeft,
  ListTree,
  MessageCircle,
  ClipboardCheck,
  CalendarClock,
  Workflow,
  BookOpen,
  FileText,
  MonitorPlay,
  Rocket,
  Headphones,
  BarChart3,
  Ticket,
  Send,
  X
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ContactForm from '../ContactForm';

const PageHeader = ({ onOpenPopup, t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; }}
        />
      </a>
      <div className="flex items-center gap-4">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {t('common.free_diagnostic')}
        </button>
      </div>
    </div>
  </header>
);

const MetodologiaPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  const FeatureCard = ({ icon: Icon, title, text, colorClass }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 h-full">
      <div className="flex items-center gap-3 mb-2">
        <Icon className={colorClass} size={20} />
        <h4 className="font-semibold text-[#141414]">{title}</h4>
      </div>
      <p className="text-sm text-gray-700">{text}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-transparent font-inter">
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label={t('metodologia.popup_close')}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('metodologia.popup_title')}</h3>
              <p className="text-gray-600 mb-6">{t('metodologia.popup_desc')}</p>
              <ContactForm 
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}
      
      <PageHeader onOpenPopup={openPopup} t={t} />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-4">
              {t('metodologia.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              {t('metodologia.subtitle')}
            </p>
          </div>

          <div className="space-y-12">
            
            {/* PASO 1 */}
            <div className="relative">
              <div className="hidden md:block absolute top-5 left-5 w-0.5 h-full bg-gray-200 -z-10"></div>
              <div className="relative flex items-start gap-5 md:gap-6">
                <div className="relative z-10 flex items-center justify-center w-11 h-11 bg-blue-600 text-white rounded-full flex-shrink-0">
                  <Map size={24} />
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-2xl font-bold text-[#141414] mb-2">{t('metodologia.steps.step1.title')}</h3>
                  <p className="text-gray-600 mb-6">
                    {t('metodologia.steps.step1.desc')}
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <Zap className="text-blue-600" size={20} />
                        <h4 className="font-semibold text-[#141414]">{t('metodologia.steps.step1.pathA.title')}</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{t('metodologia.steps.step1.pathA.desc')}</p>
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {t('metodologia.steps.step1.pathA.tag')}
                      </span>
                      <ul className="mt-3 space-y-1 text-sm text-gray-700">
                        {t('metodologia.steps.step1.pathA.points', { returnObjects: true })?.map?.((p, i) => (
                          <li key={i}>• {p}</li>
                        )) || null}
                      </ul>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <FileCheck className="text-blue-600" size={20} />
                        <h4 className="font-semibold text-[#141414]">{t('metodologia.steps.step1.pathB.title')}</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{t('metodologia.steps.step1.pathB.desc')}</p>
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {t('metodologia.steps.step1.pathB.tag')}
                      </span>
                       <ul className="mt-3 space-y-1 text-sm text-gray-700">
                        {t('metodologia.steps.step1.pathB.points', { returnObjects: true })?.map?.((p, i) => (
                          <li key={i}>• {p}</li>
                        )) || null}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* PASO 2 */}
            <div className="relative">
              <div className="hidden md:block absolute top-5 left-5 w-0.5 h-full bg-gray-200 -z-10"></div>
              <div className="relative flex items-start gap-5 md:gap-6">
                <div className="relative z-10 flex items-center justify-center w-11 h-11 bg-green-600 text-white rounded-full flex-shrink-0">
                  <LayoutGrid size={24} />
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-2xl font-bold text-[#141414] mb-2">{t('metodologia.steps.step2.title')}</h3>
                  <p className="text-gray-600 mb-6">
                    {t('metodologia.steps.step2.desc')}
                  </p>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-4">
                      <h4 className="font-bold text-lg text-green-700">{t('metodologia.steps.step2.platform_name')}</h4>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                        <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                        <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-1/3 bg-gray-100 rounded p-2">
                        <p className="text-xs font-semibold text-gray-700">{t('metodologia.steps.step2.project')}</p>
                        <div className="w-full bg-gray-300 h-2 rounded-full my-2">
                          <div className="w-3/4 bg-green-600 h-2 rounded-full"></div>
                        </div>
                        <p className="text-xs text-gray-500">75% {t('metodologia.steps.step2.completed')}</p>
                      </div>
                      <div className="w-2/3 bg-gray-100 rounded p-2">
                         <p className="text-xs font-semibold text-gray-700">{t('metodologia.steps.step2.pending_tasks')}</p>
                         {t('metodologia.steps.step2.tasks', { returnObjects: true })?.map?.((task, i) => (
                           <p key={i} className="text-xs text-gray-500 mt-1">• {task}</p>
                         )) || null}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="bg-green-50 border border-green-200 rounded p-2">
                        <div className="flex items-center gap-2">
                          <ListTree className="text-green-700" size={16} />
                          <p className="text-xs font-semibold text-green-800">{t('metodologia.steps.step2.features.gantt')}</p>
                        </div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded p-2">
                        <div className="flex items-center gap-2">
                          <MessageCircle className="text-green-700" size={16} />
                          <p className="text-xs font-semibold text-green-800">{t('metodologia.steps.step2.features.chat')}</p>
                        </div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded p-2">
                        <div className="flex items-center gap-2">
                          <Ticket className="text-green-700" size={16} />
                          <p className="text-xs font-semibold text-green-800">{t('metodologia.steps.step2.features.tickets')}</p>
                        </div>
                      </div>
                       <div className="bg-green-50 border border-green-200 rounded p-2">
                        <div className="flex items-center gap-2">
                          <CalendarClock className="text-green-700" size={16} />
                          <p className="text-xs font-semibold text-green-800">{t('metodologia.steps.step2.features.meeting')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* PASO 3 */}
            <div className="relative">
              <div className="hidden md:block absolute top-5 left-5 w-0.5 h-full bg-gray-200 -z-10"></div>
              <div className="relative flex items-start gap-5 md:gap-6">
                <div className="relative z-10 flex items-center justify-center w-11 h-11 bg-purple-600 text-white rounded-full flex-shrink-0">
                  <PackageCheck size={24} />
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-2xl font-bold text-[#141414] mb-2">{t('metodologia.steps.step3.title')}</h3>
                  <p className="text-gray-600 mb-6">
                    {t('metodologia.steps.step3.desc')}
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FeatureCard
                      icon={Workflow}
                      colorClass="text-purple-600"
                      title={t('metodologia.steps.step3.features.bpmn.title')}
                      text={t('metodologia.steps.step3.features.bpmn.desc')}
                    />
                    <FeatureCard
                      icon={BookOpen}
                      colorClass="text-purple-600"
                      title={t('metodologia.steps.step3.features.manuals.title')}
                      text={t('metodologia.steps.step3.features.manuals.desc')}
                    />
                    <FeatureCard
                      icon={FileText}
                      colorClass="text-purple-600"
                      title={t('metodologia.steps.step3.features.tech_docs.title')}
                      text={t('metodologia.steps.step3.features.tech_docs.desc')}
                    />
                     <FeatureCard
                      icon={MonitorPlay}
                      colorClass="text-purple-600"
                      title={t('metodologia.steps.step3.features.training.title')}
                      text={t('metodologia.steps.step3.features.training.desc')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* PASO 4 */}
            <div className="relative">
              <div className="hidden md:block absolute top-5 left-5 w-0.5 h-full bg-gray-200 -z-10"></div>
              <div className="relative flex items-start gap-5 md:gap-6">
                <div className="relative z-10 flex items-center justify-center w-11 h-11 bg-teal-600 text-white rounded-full flex-shrink-0">
                  <BarChart3 size={24} />
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-2xl font-bold text-[#141414] mb-2">{t('metodologia.steps.step4.title')}</h3>
                  <p className="text-gray-600 mb-6">
                    {t('metodologia.steps.step4.desc')}
                  </p>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center justify-between pb-3 border-b border-gray-200 mb-4">
                      <h4 className="font-bold text-lg text-teal-700">{t('metodologia.steps.step4.tablero_title')}</h4>
                      <div className="text-xs font-semibold text-gray-500">{t('metodologia.steps.step4.cycle')}</div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {t('metodologia.steps.step4.phases', { returnObjects: true })?.map?.((phase, i) => (
                        <span key={i} className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-800 text-sm font-semibold px-3 py-1 rounded-full">
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-teal-600 text-white text-xs font-bold">{i+1}</span>
                          {phase}
                        </span>
                      )) || null}
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                        <div className="text-xs font-semibold text-teal-800">{t('metodologia.steps.step4.kpis.title')}</div>
                        <div className="mt-3 space-y-2 text-sm text-gray-700">
                          <div className="flex items-center justify-between">
                            <span>{t('metodologia.steps.step4.kpis.cycle_time')}</span>
                            <span className="font-bold text-gray-900">↓</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>{t('metodologia.steps.step4.kpis.rework')}</span>
                            <span className="font-bold text-gray-900">↓</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>{t('metodologia.steps.step4.kpis.productivity')}</span>
                            <span className="font-bold text-gray-900">↑</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <ClipboardCheck className="text-teal-600 flex-shrink-0" size={18} />
                          <div className="font-semibold text-gray-900">{t('metodologia.steps.step4.backlog.title')}</div>
                        </div>
                        <ul className="mt-3 space-y-2 text-sm text-gray-700">
                          {t('metodologia.steps.step4.backlog.points', { returnObjects: true })?.map?.((point, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <Check className="text-teal-600 mt-0.5 flex-shrink-0" size={16} />
                              <span>{point}</span>
                            </li>
                          )) || null}
                        </ul>
                      </div>

                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <CalendarClock className="text-teal-600 flex-shrink-0" size={18} />
                          <div className="font-semibold text-gray-900">{t('metodologia.steps.step4.iterations.title')}</div>
                        </div>
                        <div className="mt-3 space-y-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-teal-600 h-2 rounded-full w-2/3"></div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <div className="text-xs text-gray-500 font-semibold">{t('metodologia.steps.step4.iterations.plan')}</div>
                              <div className="text-sm font-bold text-gray-900 mt-1">{t('metodologia.steps.step4.iterations.sprint')}</div>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <div className="text-xs text-gray-500 font-semibold">{t('metodologia.steps.step4.iterations.measure')}</div>
                              <div className="text-sm font-bold text-gray-900 mt-1">{t('metodologia.steps.step4.iterations.result')}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* PASO 5 */}
            <div className="relative">
              <div className="relative flex items-start gap-5 md:gap-6">
                <div className="relative z-10 flex items-center justify-center w-11 h-11 bg-orange-600 text-white rounded-full flex-shrink-0">
                  <LifeBuoy size={24} />
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-2xl font-bold text-[#141414] mb-2">{t('metodologia.steps.step5.title')}</h3>
                  <p className="text-gray-600 mb-6">
                    {t('metodologia.steps.step5.desc')}
                  </p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <div className="flex items-center justify-between pb-3 border-b border-gray-200 mb-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <Rocket className="text-orange-600 flex-shrink-0" size={18} />
                          <h4 className="font-bold text-lg text-orange-700 truncate">{t('metodologia.steps.step5.go_live.title')}</h4>
                        </div>
                        <div className="text-xs font-semibold text-gray-500">{t('metodologia.steps.step5.go_live.badge')}</div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {t('metodologia.steps.step5.go_live.phases', { returnObjects: true })?.map?.((phase, i) => (
                          <span key={i} className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 text-sm font-semibold px-3 py-1 rounded-full">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-600 text-white text-xs font-bold">{i+1}</span>
                            {phase}
                          </span>
                        )) || null}
                      </div>

                      <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-orange-600 h-2 rounded-full w-1/2"></div>
                        </div>
                        <ul className="mt-4 space-y-2 text-sm text-gray-700">
                          {t('metodologia.steps.step5.go_live.points', { returnObjects: true })?.map?.((point, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <Check className="text-orange-600 mt-0.5 flex-shrink-0" size={16} />
                              <span>{point}</span>
                            </li>
                          )) || null}
                        </ul>
                      </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-5">
                      <div className="flex items-center justify-between pb-3 border-b border-gray-200 mb-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <Headphones className="text-orange-600 flex-shrink-0" size={18} />
                          <h4 className="font-bold text-lg text-orange-700 truncate">{t('metodologia.steps.step5.support.title')}</h4>
                        </div>
                        <div className="text-xs font-semibold text-gray-500">{t('metodologia.steps.step5.support.badge')}</div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {t('metodologia.steps.step5.support.phases', { returnObjects: true })?.map?.((phase, i) => (
                          <span key={i} className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 text-gray-800 text-sm font-semibold px-3 py-1 rounded-full">
                            {i === 0 && <Ticket className="text-orange-600 flex-shrink-0" size={14} />}
                            {i === 1 && <MessageCircle className="text-orange-600 flex-shrink-0" size={14} />}
                            {i === 2 && <CalendarClock className="text-orange-600 flex-shrink-0" size={14} />}
                            {phase}
                          </span>
                        )) || null}
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-3">
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <div className="text-xs font-semibold text-orange-800">{t('metodologia.steps.step5.support.response.title')}</div>
                          <div className="mt-1 text-lg font-extrabold text-gray-900">{t('metodologia.steps.step5.support.response.value')}</div>
                          <div className="mt-1 text-xs text-gray-600">{t('metodologia.steps.step5.support.response.desc')}</div>
                        </div>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <div className="text-xs font-semibold text-orange-800">{t('metodologia.steps.step5.support.resolution.title')}</div>
                          <div className="mt-1 text-lg font-extrabold text-gray-900">{t('metodologia.steps.step5.support.resolution.value')}</div>
                          <div className="mt-1 text-xs text-gray-600">{t('metodologia.steps.step5.support.resolution.desc')}</div>
                        </div>
                      </div>

                      <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="font-semibold text-gray-900">{t('metodologia.steps.step5.support.coverage_title')}</div>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
                          {t('metodologia.steps.step5.support.coverage_items', { returnObjects: true })?.map?.((item, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <Check className="text-orange-600 mt-0.5 flex-shrink-0" size={16} />
                              <span>{item}</span>
                            </div>
                          )) || null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="max-w-4xl mx-auto mt-24 mb-12">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold text-[#141414] mb-4">
              {t('metodologia.cta.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto mb-8">
              {t('metodologia.cta.desc')}
            </p>
            <button
              onClick={openPopup}
              className="inline-flex items-center justify-center gap-2 bg-[#141414] text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition font-semibold"
            >
              <Send size={18} />
              {t('metodologia.cta.button')}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default MetodologiaPage;

